/**
 * TikTok Pixel tracking utilities
 * This file centralizes all TikTok pixel event tracking
 * to maintain consistency and make updates easier.
 */

import CryptoJS from 'crypto-js';

/* global ttq */

/**
 * Safety check for ttq function availability
 * @returns {boolean} Whether ttq is available
 */
function isTtqAvailable() {
  return typeof window !== 'undefined' && 
    typeof window.ttq === 'object' &&
    typeof window.ttq.track === 'function';
}

/**
 * Hash PII data with SHA-256 as required by TikTok
 * @param {string} data - Data to hash
 * @returns {string} SHA-256 hash of the data
 */
function hashData(data) {
  if (!data) return '';
  return CryptoJS.SHA256(data.toLowerCase().trim()).toString();
}

/**
 * Identify user for TikTok pixel (call when user logs in or provides contact info)
 * @param {Object} userData - User data containing email, phone, userId
 * @returns {boolean} Whether identification was successful
 */
export function identifyUser(userData = {}) {
  if (isTtqAvailable()) {
    const identifyData = {};
    
    if (userData.email) {
      identifyData.email = hashData(userData.email);
    }
    
    if (userData.phone) {
      identifyData.phone_number = hashData(userData.phone);
    }
    
    if (userData.userId) {
      identifyData.external_id = hashData(userData.userId.toString());
    }
    
    if (Object.keys(identifyData).length > 0) {
      ttq.identify(identifyData);
      return true;
    }
  }
  return false;
}

/**
 * Track creator signup/registration conversion
 * @param {Object} userData - User data for identification
 * @returns {boolean} Whether tracking was successful
 */
export function trackCreatorSignup(userData = {}) {
  if (isTtqAvailable()) {
    // Identify user if data is provided
    if (userData.email || userData.phone || userData.userId) {
      identifyUser(userData);
    }
    
    ttq.track('CompleteRegistration', {
      contents: [{
        content_id: 'registration',
        content_type: 'product_group',
        content_name: 'Creator Registration'
      }],
      value: 5.0,
      currency: 'USD'
    });
    return true;
  }
  return false;
}

/**
 * Track customer signup/registration conversion
 * @param {Object} userData - User data for identification
 * @returns {boolean} Whether tracking was successful
 */
export function trackCustomerSignup(userData = {}) {
  if (isTtqAvailable()) {
    // Identify user if data is provided
    if (userData.email || userData.phone || userData.userId) {
      identifyUser(userData);
    }
    
    ttq.track('CompleteRegistration', {
      contents: [{
        content_id: 'customer_registration',
        content_type: 'product_group',
        content_name: 'Customer Registration'
      }],
      value: 3.0,
      currency: 'USD'
    });
    return true;
  }
  return false;
}

/**
 * Track main website page views (except creator stores)
 * @returns {boolean} Whether tracking was successful
 */
export function trackMainWebsitePageview() {
  if (isTtqAvailable()) {
    ttq.track('ViewContent', {
      contents: [{
        content_id: 'main_website',
        content_type: 'product_group',
        content_name: 'Main Website'
      }],
      value: 0.25,
      currency: 'USD'
    });
    return true;
  }
  return false;
}

/**
 * Track creator store page views
 * @returns {boolean} Whether tracking was successful
 */
export function trackCreatorPageView() {
  if (isTtqAvailable()) {
    ttq.track('ViewContent', {
      contents: [{
        content_id: 'creator_store',
        content_type: 'product_group',
        content_name: 'Creator Store'
      }],
      value: 0.75,
      currency: 'USD'
    });
    return true;
  }
  return false;
}

/**
 * Track when users view a product
 * @param {Object} product - Product information
 * @returns {boolean} Whether tracking was successful
 */
export function trackViewProduct(product = {}) {
  if (isTtqAvailable()) {
    ttq.track('ViewContent', {
      contents: [{
        content_id: product.id || product._id || '',
        content_type: 'product',
        content_name: product.title || product.name || 'Product',
        content_category: product.category || 'Uncategorized',
        price: product.price || 0
      }],
      value: product.price || 0,
      currency: product.currency || 'USD',
      description: product.description || ''
    });
    return true;
  }
  return false;
}

/**
 * Track when users add products to cart
 * @param {Object} product - Product information 
 * @param {number} quantity - Quantity added to cart
 * @returns {boolean} Whether tracking was successful
 */
export function trackAddToCart(product = {}, quantity = 1) {
  if (isTtqAvailable()) {
    const value = (product.price || 0) * quantity;
    
    ttq.track('AddToCart', {
      contents: [{
        content_id: product.id || product._id || '',
        content_type: 'product',
        content_name: product.title || product.name || 'Product',
        content_category: product.category || 'Uncategorized',
        price: product.price || 0,
        num_items: quantity
      }],
      value: value,
      currency: product.currency || 'USD',
      description: product.description || ''
    });
    return true;
  }
  return false;
}

/**
 * Track when users add products to wishlist
 * @param {Object} product - Product information
 * @returns {boolean} Whether tracking was successful
 */
export function trackAddToWishlist(product = {}) {
  if (isTtqAvailable()) {
    ttq.track('AddToWishlist', {
      contents: [{
        content_id: product.id || product._id || '',
        content_type: 'product',
        content_name: product.title || product.name || 'Product'
      }],
      value: product.price || 0,
      currency: product.currency || 'USD'
    });
    return true;
  }
  return false;
}

/**
 * Track search events
 * @param {string} searchTerm - The search query
 * @param {Array} results - Array of search result products
 * @returns {boolean} Whether tracking was successful
 */
export function trackSearch(searchTerm, results = []) {
  if (isTtqAvailable()) {
    const contents = results.slice(0, 5).map(product => ({
      content_id: product.id || product._id || '',
      content_type: 'product',
      content_name: product.title || product.name || 'Product'
    }));

    const totalValue = results.reduce((sum, product) => sum + (product.price || 0), 0);

    ttq.track('Search', {
      contents: contents,
      value: totalValue,
      currency: 'USD',
      search_string: searchTerm
    });
    return true;
  }
  return false;
}

/**
 * Track begin checkout events from creator store product pages
 * @param {Array} cartItems - Array of cart items
 * @returns {boolean} Whether tracking was successful
 */
export function trackInitiateCheckout(cartItems = []) {
  if (isTtqAvailable()) {
    const contents = cartItems.map(item => ({
      content_id: item.product?.id || item.product?._id || item.productId || '',
      content_type: 'product',
      content_name: item.product?.title || item.product?.name || item.title || 'Product',
      content_category: item.product?.category || 'Uncategorized',
      price: item.product?.price || item.price || 0,
      num_items: item.quantity || 1
    }));

    const totalValue = cartItems.reduce((sum, item) => 
      sum + ((item.product?.price || item.price || 0) * (item.quantity || 1)), 0
    );

    ttq.track('InitiateCheckout', {
      contents: contents,
      value: totalValue,
      currency: 'USD',
      description: 'User initiated checkout process'
    });
    return true;
  }
  return false;
}

/**
 * Track begin checkout for single product (buy now)
 * @param {Object} product - Product information
 * @param {number} quantity - Quantity being purchased
 * @returns {boolean} Whether tracking was successful
 */
export function trackBeginCheckout(product = {}, quantity = 1) {
  if (isTtqAvailable()) {
    const value = (product.price || 0) * quantity;
    
    ttq.track('InitiateCheckout', {
      contents: [{
        content_id: product.id || product._id || '',
        content_type: 'product',
        content_name: product.title || product.name || 'Product',
        content_category: product.category || 'Uncategorized',
        price: product.price || 0,
        num_items: quantity
      }],
      value: value,
      currency: product.currency || 'USD',
      description: 'User clicked buy now'
    });
    return true;
  }
  return false;
}

/**
 * Track when payment information is added
 * @param {Array} cartItems - Array of cart items
 * @returns {boolean} Whether tracking was successful
 */
export function trackAddPaymentInfo(cartItems = []) {
  if (isTtqAvailable()) {
    const contents = cartItems.map(item => ({
      content_id: item.product?.id || item.product?._id || item.productId || '',
      content_type: 'product',
      content_name: item.product?.title || item.product?.name || item.title || 'Product',
      price: item.product?.price || item.price || 0,
      num_items: item.quantity || 1
    }));

    const totalValue = cartItems.reduce((sum, item) => 
      sum + ((item.product?.price || item.price || 0) * (item.quantity || 1)), 0
    );

    ttq.track('AddPaymentInfo', {
      contents: contents,
      value: totalValue,
      currency: 'USD',
      description: 'User added payment information'
    });
    return true;
  }
  return false;
}

/**
 * Track purchase completion
 * @param {Object} orderData - Order data containing transaction info
 * @returns {boolean} Whether tracking was successful
 */
export function trackPurchase(orderData = {}) {
  if (isTtqAvailable()) {
    const contents = (orderData.items || []).map(item => ({
      content_id: item.product?.id || item.product?._id || item.productId || '',
      content_type: 'product',
      content_name: item.product?.title || item.product?.name || item.title || 'Product',
      content_category: item.product?.category || 'Uncategorized',
      price: item.product?.price || item.price || 0,
      num_items: item.quantity || 1
    }));

    ttq.track('Purchase', {
      contents: contents,
      value: orderData.totalAmount || orderData.total || 0,
      currency: orderData.currency || 'USD',
      description: `Order ${orderData.orderNumber || orderData.orderId || ''} completed`,
      status: 'completed'
    });
    return true;
  }
  return false;
}

/**
 * Track customer purchases from creator stores
 * @param {Object} orderData - Order data containing transaction info
 * @returns {boolean} Whether tracking was successful
 */
export function trackCustomerPurchase(orderData = {}) {
  return trackPurchase(orderData);
}

/**
 * Track lead generation events (like email signup)
 * @param {Object} data - Lead data
 * @returns {boolean} Whether tracking was successful
 */
export function trackLead(data = {}) {
  if (isTtqAvailable()) {
    ttq.track('CompleteRegistration', {
      contents: [{
        content_id: 'lead_generation',
        content_type: 'product_group',
        content_name: data.source || 'Email Signup'
      }],
      value: 3.0,
      currency: 'USD'
    });
    return true;
  }
  return false;
}

/**
 * Track custom events that don't fit standard categories
 * @param {string} eventName - Name of the custom event
 * @param {Object} eventData - Custom event data
 * @returns {boolean} Whether tracking was successful
 */
export function trackCustomEvent(eventName, eventData = {}) {
  if (isTtqAvailable() && eventName) {
    ttq.track(eventName, eventData);
    return true;
  }
  return false;
}
